import React from 'react';
import { useDispatch, useSelector } from 'react-redux/es';

import moment from 'moment';
import Swal from 'sweetalert2';
import Offcanvas from 'react-bootstrap/Offcanvas';

import { useUiStore } from '../hooks/useUiStore';
import { addInsightAction, getTimeline } from '../../store/ai2';
import { ActionLog } from './Insights/components/ActionLog';
import { useLocation } from 'react-router-dom';

import './offCanvas.scss';
import InsightItemForm from './Insights/InsightItemForm';
import { useInsights } from './Insights/hooks/useInsights';

import fullscreenEnterIcon from '../../assets/img/fullscreen-enter.png';
import shieldIcon from '../../assets/img/shield.png';
import redBellIcon from '../../assets/img/redBell.png';
import yellowBellIcon from '../../assets/img/yellowBell.png';

// action status nomenclature
const actionStatus = {
  E: 'Closed',
  A: 'Closed',
  D: 'Closed',
  C: 'Closed',
  null: 'Ongoing',
  I: 'Ignored',
};

export const InsightsOffCanvas = (props) => {
  // all insights from store
  const { insights, lastUpdatedInsight } = useSelector(
    (state) => state.insights,
  );

  const { handleSubmitInsight } = useInsights();
  // Off Canvas state from ui store
  const { isInsightDetailsOpen, insightDetailsContent, closeInsightDetails } =
    useUiStore();
  const { selectedTenancy } = useSelector((state) => state.clinicianProfile);
  const isAbleToViewAllInsights = React.useMemo(() => {
    if (!selectedTenancy?.features?.patientProfileTabSettings) {
      return true;
    }
    return Boolean(
      selectedTenancy?.features?.patientProfileTabSettings
        ?.showInsightsAndActions,
    );
  }, [selectedTenancy?.features?.patientProfileTabSettings]);

  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const access_token = searchParams.get('access_token');

  const viewAllInsights = () => {
    // close Off Canvas
    if (typeof props.sendToParent === 'function') {
      props.sendToParent('2');
    }
    closeInsightDetails();

    // Delay scrolling to top
    setTimeout(() => {
      document.body.scrollTop = 0;
      document.documentElement.scrollTop = 0;
    }, 10);
  };

  // we need this for dispatch actions
  const dispatch = useDispatch();

  // handle for "Save" button (submit form)
  const handleInsightSubmit = async (action) => {
    const success = await handleSubmitInsight(action);
    if (success) {
      Swal.fire({
        icon: 'success',
        title: 'Action has been saved',
        showConfirmButton: true,
      });
    } else {
      Swal.fire({
        icon: 'warning',
        text: 'You must select an action',
        showConfirmButton: true,
      });
    }

    await dispatch(
      getTimeline(
        access_token,
        moment().add(-2, 'year').format(),
        moment().add(0, 'year').format(),
        selectedTenancy,
        true,
      ),
    );
  };

  // handle for "Mark insight as closed" button
  const handleCloseInsight = async (event, action) => {
    event.preventDefault();
    action.action_status = 'C';
    const queryParams = new URLSearchParams(window.location.search);
    action.access_token = queryParams.get('access_token');
    await dispatch(addInsightAction(action, selectedTenancy));
    Swal.fire({
      icon: 'success',
      title: 'Action has been closed',
      showConfirmButton: true,
    });
    await dispatch(
      getTimeline(
        access_token,
        moment().add(-2, 'year').format(),
        moment().add(0, 'year').format(),
        selectedTenancy,
        true,
      ),
    );
    closeInsightDetails();
  };

  // handle for "Cancel" button
  const handleCancel = (event) => {
    event.preventDefault();

    // close Off Canvas
    closeInsightDetails();
  };

  // if Off Canvas is open
  if (isInsightDetailsOpen) {
    let item = 0;

    // if insight comes from timeline event
    if (insightDetailsContent.insight_id) {
      item = insights.find(
        (insight) => insight.id == insightDetailsContent.insight_id,
      );
    } else {
      item = insights.find((insight) => insight.id == insightDetailsContent.id);
    }
    if (!item) {
      return <></>;
    }

    // Rendering //
    return (
      <>
        <Offcanvas
          show={isInsightDetailsOpen}
          onHide={closeInsightDetails}
          placement="end"
          backdrop={false}
          scroll={true}
          className={
            'insight-border ' + (item?.risk_status === 'h' ? 'red' : 'yellow')
          }
        >
          <Offcanvas.Header>
            <button
              type="button"
              className="btn-close me-auto"
              aria-label="Close"
              onClick={closeInsightDetails}
            />
            {isAbleToViewAllInsights && (
              <button
                className="btn btnPrimary btn-sm"
                onClick={viewAllInsights}
              >
                View all insights
                <img
                  style={{ paddingLeft: '10px', paddingRight: '5px' }}
                  alt=""
                  src={fullscreenEnterIcon}
                />
              </button>
            )}
          </Offcanvas.Header>
          <Offcanvas.Body>
            <h2 className="offcanvas-title">
              {item?.action_status === 'E' ||
              item?.action_status === 'A' ||
              item?.action_status === 'D' ||
              item?.action_status === 'S' ||
              item?.action_status === 'C' ? (
                <img
                  className="mr-15 shield"
                  src={shieldIcon}
                  alt="shield icon"
                />
              ) : item?.risk_status === 'h' ? (
                <img className="mr-15" src={redBellIcon} alt="Red Bell" />
              ) : (
                <img className="mr-15" src={yellowBellIcon} alt="Yellow Bell" />
              )}
              Insight details
            </h2>

            <div className="offcanvas-inner">
              <div className="group">
                <div>
                  <div className="status">
                    {item.action_status === 'E' ||
                    item.action_status === 'A' ||
                    item.action_status === 'D' ||
                    item.action_status === 'C' ||
                    item.action_status === 'S'
                      ? 'Closed'
                      : item.action_status === null
                        ? 'Active'
                        : item.action_status === 'I'
                          ? 'Ignored'
                          : 'no data'}
                  </div>
                  <div className="date">
                    {item.when_created
                      ? moment(new Date(item.when_created)).format(
                          'DD MMM YYYY',
                        ) + ' - '
                      : ''}
                    {item.when_ended
                      ? moment(new Date(item.when_ended)).format('DD MMM YYYY')
                      : ''}
                  </div>
                </div>
                <div className="title"> {item.why_created}</div>

                <div className="title">
                  {/*<SummaryLog item={item} content={insightDetailsContent}/>*/}
                </div>
              </div>

              <div className="group">
                <ActionLog item={item} key={item.id} short={true} />
              </div>

              <div className="group">
                <InsightItemForm
                  key={item.id}
                  item={item}
                  handleInsightSubmit={handleInsightSubmit}
                  handleCloseInsight={handleCloseInsight}
                  handleCancel={handleCancel}
                  popout
                />
              </div>
            </div>
          </Offcanvas.Body>
        </Offcanvas>
      </>
    );
  } else return null;
};
